import { useCallback, useEffect, useState } from 'react';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TiendaPerfectaDistributor,
  TiendaPerfectaDistributorsResponse,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import {
  ROUTE_REPORT_BLOCKED_REASON_BY_CODE,
  RouteReportBlockedReason,
} from '../types';

interface UseRouteReportScopeReturn {
  loading: boolean;
  distributors: TiendaPerfectaDistributor[];
  /** `undefined` es "Todos", que es el valor por defecto. */
  selectedDistributorId: string | undefined;
  setSelectedDistributorId: (id: string | undefined) => void;
  /** Con un solo distribuidor no hay nada que elegir, pero sí que mostrar. */
  singleDistributor: boolean;
  blockedReason: RouteReportBlockedReason | null;
}

/**
 * Qué distribuidores alcanza el usuario. `/distributors/scoped` ya aplica el
 * tier de los roles, así que la vista no necesita conocerlo: solo le importa
 * cuántos son.
 *
 * Con uno solo —el caso de un rol `low`— se selecciona ese y el filtro queda
 * bloqueado. Con varios arranca en "Todos".
 */
export function useRouteReportScope(): UseRouteReportScopeReturn {
  const [loading, setLoading] = useState(true);
  const [distributors, setDistributors] = useState<TiendaPerfectaDistributor[]>(
    []
  );
  const [selectedDistributorId, setSelectedDistributorId] = useState<
    string | undefined
  >(undefined);
  const [blockedReason, setBlockedReason] =
    useState<RouteReportBlockedReason | null>(null);

  const fetchScope = useCallback(async () => {
    setLoading(true);

    const response = await TiendaPerfectaService.getScopedDistributors({
      sortBy: 'tradeName',
      sortDir: 'asc',
    });

    if (response.success) {
      const list =
        (response.data as TiendaPerfectaDistributorsResponse)?.distributors ??
        [];

      setDistributors(list);
      // Con acceso pero sin distribuidores asignados no hay error: es un 200 con
      // lista vacía, y aun así no hay nada que reportar.
      setBlockedReason(list.length === 0 ? 'no_distributors' : null);
      // Uno solo se preselecciona; con varios se arranca en "Todos".
      setSelectedDistributorId(list.length === 1 ? list[0].id : undefined);
    } else {
      const error = response.data as ApiErrorData;
      // Se ramifica por `code`, nunca por el mensaje: el texto puede cambiar y
      // no está garantizado en español.
      setBlockedReason(
        ROUTE_REPORT_BLOCKED_REASON_BY_CODE[error?.code] ?? 'generic'
      );
      setDistributors([]);
      setSelectedDistributorId(undefined);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchScope();
  }, [fetchScope]);

  return {
    loading,
    distributors,
    selectedDistributorId,
    setSelectedDistributorId,
    singleDistributor: distributors.length === 1,
    blockedReason,
  };
}
